import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import Select from "react-select";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";

import {
  createCountryAdmin,
  getAllCountryAdmins,
  updateCountryAdmin,
  deleteCountryAdmin,
  getAllCountries,
} from "api";

import { handleError } from "utils/errorHandler";

const CountryAdmin = () => {
  document.title = "Country Admin | EventoX";

  const [contact, setContact] = useState(null);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [country, setCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await getAllCountryAdmins();
      const filtered = Array.isArray(res)
        ? res.filter((u) => Number(u.userRole) === 5)
        : [];
      setUsers(filtered);
      setAllUsers(filtered);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await getAllCountries();
      setCountry(res || []);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchCountries();
  }, []);

  const toggle = () => setModal(!modal);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: contact?.name || "",
      email: contact?.email || "",
      phoneNo: contact?.phoneNo || "",
      password: "",
      location: contact?.location || "",
      status: contact?.status || "active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter full name"),
      email: Yup.string().email("Invalid email").required("Please enter email"),
      phoneNo: Yup.string().required("Please enter phone number"),
      location: Yup.string().required("Please select country"),
      ...(isEdit
        ? {}
        : { password: Yup.string().required("Please enter password") }),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      const data = new FormData();
      data.append("name", values.name);
      data.append("phoneNo", values.phoneNo);
      data.append("email", values.email);
      data.append("location", selectedCountry);

      try {
        if (isEdit) {
          data.append("status", values.status);
          await updateCountryAdmin(contact.userId, data);
          toast.success("Country Admin updated successfully");
        } else {
          data.append("username", values.email);
          data.append("password", values.password);
          data.append("organizerId", 0);
          data.append("userRole", 5);
          await createCountryAdmin(data);
          toast.success("Country Admin created successfully");
        }

        await fetchAdmins();
        toggle();
        validation.resetForm();
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleUserClick = (user) => {
    setContact(user);
    setSelectedCountry(user.location);
    setIsEdit(true);
    toggle();
  };

  const handleAddClick = () => {
    setContact(null);
    setSelectedCountry(null);
    setIsEdit(false);
    toggle();
  };

  const handleDeleteUser = async () => {
    if (!contact?.userId) return;
    try {
      setLoading(true);
      await deleteCountryAdmin(contact.userId);
      toast.success("Country Admin deleted successfully");
      await fetchAdmins();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      setDeleteModal(false);
    }
  };

  const handleSearch = (value) => {
    const keyword = value.toLowerCase();
    if (!keyword) {
      setUsers(allUsers);
      return;
    }

    const filtered = allUsers.filter(
      (u) =>
        u.name?.toLowerCase().includes(keyword) ||
        u.email?.toLowerCase().includes(keyword) ||
        u.phoneNo?.toLowerCase().includes(keyword)
    );

    setUsers(filtered);
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Phone",
        accessorKey: "phoneNo",
      },
      {
        header: "Country",
        accessorFn: (row) => {
          const countryObj = country.find(
            (c) => String(c.countryId) === String(row.location)
          );
          return countryObj ? countryObj.countryName : "-";
        },
        id: "country",
        cell: (cell) => cell.getValue(),
      },
      {
        header: "Status",
        accessorKey: "status",
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          if (val === undefined || val === null) return false;
          return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
        },
        cell: (cell) => (
          <span
            className={`badge badge-soft-${
              cell.getValue() === "active" ? "success" : "danger"
            } font-size-11 text-capitalize`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: 'Created Date',
        accessorKey: 'createdAt',
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          if (!val) return false;
          const display = new Date(val).toLocaleDateString();
          return display.toLowerCase().includes(String(filterValue).toLowerCase());
        },
        cell: (cell) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleDateString() : "-";
        }
      },
      {
        header: "Action",
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <div
              className="text-success cursor-pointer"
              onClick={() => handleUserClick(cellProps.row.original)}
            >
                      <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
            </div>
            <div
              className="text-danger cursor-pointer"
              onClick={() => {
                setContact(cellProps.row.original);
                setDeleteModal(true);
              }}
            >
              <i className="mdi mdi-delete font-size-18" />
            </div>
          </div>
        ),
      },
    ],
    [country]
  );

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Country Admin" />

          <Row>
            {isLoading ? (
              <Spinners setLoading={setLoading} />
            ) : (
              <Col lg="12">
                <Card>
                  <CardBody className="p-4">

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div
                        className="position-relative"
                        style={{ maxWidth: "300px", width: "100%" }}
                      >
                        <i
                          className="mdi mdi-magnify position-absolute"
                          style={{
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                          }}
                        />
                        <Input
                          type="text"
                          placeholder="Search..."
                          className="ps-5"
                          onChange={(e) => handleSearch(e.target.value)}
                        />
                      </div>

                      <button
                        className="btn btn-success btn-rounded waves-effect waves-light"
                        onClick={handleAddClick}
                      >
                        <i className="mdi mdi-plus me-1" />
                        New Country Admin
                      </button>
                    </div>

                    <TableContainer
                      columns={columns}
                      data={users || []}
                      isPagination
                      tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100"
                      theadClass="table-light"
                      pagination="pagination"
                    />
                  </CardBody>
                </Card>
              </Col>
            )}

            <Modal isOpen={modal} toggle={toggle} centered>
              <ModalHeader toggle={toggle}>
                {isEdit ? "Update Country Admin" : "Create Country Admin"}
              </ModalHeader>

              <ModalBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <div className="mb-3">
                    <Label>Full Name</Label>
                    <Input
                      name="name"
                      placeholder="Enter full name"
                      value={validation.values.name}
                      onChange={validation.handleChange}
                      invalid={
                        validation.touched.name && !!validation.errors.name
                      }
                    />
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label>Email</Label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={validation.values.email}
                      onChange={validation.handleChange}
                      invalid={
                        validation.touched.email && !!validation.errors.email
                      }
                    />
                    <FormFeedback>{validation.errors.email}</FormFeedback>
                  </div>

                  {!isEdit && (
                    <div className="mb-3">
                      <Label>Password</Label>
                      <Input
                        name="password"
                        type="password"
                        placeholder="Enter secure password"
                        value={validation.values.password}
                        onChange={validation.handleChange}
                        invalid={
                          validation.touched.password &&
                          !!validation.errors.password
                        }
                      />
                      <FormFeedback>
                        {validation.errors.password}
                      </FormFeedback>
                    </div>
                  )}

                  <div className="mb-3">
                    <Label>Phone</Label>
                    <Input
                      name="phoneNo"
                      placeholder="Enter phone number"
                      value={validation.values.phoneNo}
                      onChange={validation.handleChange}
                      invalid={
                        validation.touched.phoneNo &&
                        !!validation.errors.phoneNo
                      }
                    />
                    <FormFeedback>
                      {validation.errors.phoneNo}
                    </FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label>Country</Label>
                    <Select
                      options={country.map((c) => ({
                        value: c.countryId,
                        label: c.countryName,
                      }))}
                      value={
                        country
                          .map((c) => ({
                            value: c.countryId,
                            label: c.countryName,
                          }))
                          .find((opt) => opt.value === selectedCountry) || null
                      }
                      onChange={(opt) => {
                        validation.setFieldValue("location", opt.value);
                        setSelectedCountry(opt.value);
                      }}
                      styles={{
                        menu: provided => ({ ...provided, zIndex: 9999 })
                    }}
                      placeholder="Select country"
                    />
                  </div>

                  {isEdit && (
                    <div className="mb-3">
                      <Label>Status</Label>
                      <Select
                        options={[
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" },
                        ]}
                        value={{
                          value: validation.values.status,
                          label:
                            validation.values.status === "active"
                              ? "Active"
                              : "Inactive",
                        }}
                        onChange={(opt) =>
                          validation.setFieldValue("status", opt.value)
                        }
                        styles={{
                          menu: provided => ({ ...provided, zIndex: 9999 })
                      }}
                      />
                    </div>
                  )}

                  <div className="text-end">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={isLoading}
                    >
                      {isLoading ? "Processing..." : "Save"}
                    </button>
                  </div>
                </Form>
              </ModalBody>
            </Modal>
          </Row>
        </Container>
      </div>

      <ToastContainer />
    </>
  );
};

export default withRouter(CountryAdmin);
